import { useQuery, type UseQueryResult } from "@tanstack/react-query";

import {
  queryKeyForScope,
  type EnvVarScope,
  type ToolEnvVar,
  type ToolScope,
  type WorkspaceEnvVar,
  type WorkspaceScope,
} from "./types";

import { ENDPOINTS } from "@/api/application-api";
import { useAuth } from "@/context/auth-context";
import { handleResponse } from "@/utils/errorResponseHandlers";
import { jsonAuthHeaders } from "@/utils/headers";

interface ListWorkspaceResponse {
  success: boolean;
  workspace_env_vars: WorkspaceEnvVar[];
}

interface ListToolResponse {
  success: boolean;
  tool_env_vars: ToolEnvVar[];
}

interface ListOptions {
  enabled?: boolean;
}

export function useListEnvVars(
  scope: WorkspaceScope,
  options?: ListOptions,
): UseQueryResult<WorkspaceEnvVar[]>;
export function useListEnvVars(
  scope: ToolScope,
  options?: ListOptions,
): UseQueryResult<ToolEnvVar[]>;
export function useListEnvVars(
  scope: EnvVarScope,
  options?: ListOptions,
): UseQueryResult<WorkspaceEnvVar[] | ToolEnvVar[]> {
  const auth = useAuth();
  const userToken = auth?.userToken ?? "";

  return useQuery({
    queryKey: queryKeyForScope(scope),
    enabled: options?.enabled ?? true,
    queryFn: async (): Promise<WorkspaceEnvVar[] | ToolEnvVar[]> => {
      if (scope.scope === "workspace") {
        const res = await fetch(ENDPOINTS.env_vars.workspace.list(scope.workspaceId), {
          method: "GET",
          headers: jsonAuthHeaders(userToken),
        });
        const body: ListWorkspaceResponse = await handleResponse(res);
        return body.workspace_env_vars;
      }
      const res = await fetch(ENDPOINTS.env_vars.tool.list(scope.workspaceId, scope.toolId), {
        method: "GET",
        headers: jsonAuthHeaders(userToken),
      });
      const body: ListToolResponse = await handleResponse(res);
      return body.tool_env_vars;
    },
  });
}
